import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { Search, UserPlus, CheckCircle, XCircle, Clock, Mail, Phone, MapPin, Filter, Edit2, Save, Power, UserCheck, UserX, Shield, Star, Loader2, Trash2, X, Award, Calendar, Baby, Users as UsersIcon, Package, RefreshCw, AlertTriangle, User, Image as ImageIcon, Plus } from 'lucide-react';
import axios from 'axios';
import { UploadMediaModal } from './Gallery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = {
  cavalier_proprio: 'Cavalier Propriétaire',
  cavalier_pp: 'Cavalier Prop. Partagé',
  cavalier_dp: 'Cavalier Demi-Pension',
  cavalier_forfait: 'Cavalier Forfait',
  cavalier_externe: 'Cavalier Externe',
  instructeur: 'Instructeur',
  instructeur_admin: 'Instructeur Admin'
};

const roleColors = {
  cavalier_proprio: 'bg-amber-100 text-amber-800',
  cavalier_pp: 'bg-orange-100 text-orange-800',
  cavalier_dp: 'bg-blue-100 text-blue-800',
  cavalier_forfait: 'bg-green-100 text-green-800',
  cavalier_externe: 'bg-stone-100 text-stone-800',
  instructeur: 'bg-purple-100 text-purple-800',
  instructeur_admin: 'bg-red-100 text-red-800'
};

// All available roles for the dropdown
const allRoles = [
  { value: 'cavalier_proprio', label: 'Cavalier Propriétaire', description: 'Propriétaire d\'un cheval, paye la pension' },
  { value: 'cavalier_pp', label: 'Cavalier Prop. Partagé', description: 'Monte le cheval d\'un propriétaire (pas de pension)' },
  { value: 'cavalier_dp', label: 'Cavalier Demi-Pension', description: 'Forfait 220€/mois sur cheval du club' },
  { value: 'cavalier_forfait', label: 'Cavalier Forfait', description: 'Cavalier avec un forfait de cours' },
  { value: 'cavalier_externe', label: 'Cavalier Externe', description: 'Cavalier externe au club' },
  { value: 'instructeur', label: 'Instructeur', description: 'Peut gérer les cours et les cavaliers' },
  { value: 'instructeur_admin', label: 'Instructeur Admin', description: 'Accès complet à toutes les fonctionnalités' },
];

// Composant UserCard externalisé
const UserCard = ({ user, showActions, isAdmin, onSelect, onApprove, onReject }) => {
  // Check for missing required fields (for cavaliers only)
  const isCavalier = user.role_club?.startsWith('cavalier');
  const missingFields = [];
  if (isCavalier) {
    if (!user.date_naissance) missingFields.push('Date naissance');
    if (!user.pseudo) missingFields.push('Pseudo');
    if (!user.adresse_rue && !user.adresse) missingFields.push('Adresse');
    if (!user.adresse_cp) missingFields.push('CP');
    if (!user.adresse_ville) missingFields.push('Ville');
  }
  const hasMissingFields = missingFields.length > 0;

  return (
    <Card 
      className={`card-hover cursor-pointer ${hasMissingFields ? 'border-l-4 border-l-rose-400' : ''}`}
      onClick={() => onSelect(user)}
      data-testid={`user-card-${user.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center text-lg font-medium flex-shrink-0">
            {user.prenom?.[0]}{user.nom?.[0]}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-stone-800 truncate">
                {user.prenom} {user.nom}
                {user.pseudo && <span className="font-normal text-stone-500"> ({user.pseudo})</span>}
              </h3>
              <Badge className={roleColors[user.role_club] || 'bg-stone-100'}>
                {roleLabels[user.role_club] || user.role_club}
              </Badge>
            </div>
            
            <p className="text-sm text-stone-500 truncate mt-1">{user.email}</p>
            
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {user.activation_pending ? (
                <span className="status-badge bg-amber-100 text-amber-800 border-amber-200">
                  En attente d'activation
                </span>
              ) : user.statut === 'actif' ? (
                <span className="status-badge status-active">Actif</span>
              ) : (
                <span className="status-badge status-inactive">Suspendu</span>
              )}
              
              {user.is_approved === 'en_attente' && (
                <span className="status-badge status-pending">Approbation en attente</span>
              )}
              
              {/* Missing fields warning */}
              {hasMissingFields && (
                <div className="flex items-center gap-1 text-rose-500 text-xs" title={`Manquant: ${missingFields.join(', ')}`}>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{missingFields.length} info{missingFields.length > 1 ? 's' : ''} manquante{missingFields.length > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
          
          {showActions && isAdmin && (
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => onApprove(user.id)}
                data-testid={`approve-${user.id}`}
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onReject(user.id)}
                data-testid={`reject-${user.id}`}
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const Users = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('actif'); // 'actif', 'inactif', 'tous'
  
  // Delete user state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  
  // Media upload state
  const [mediaUploadOpen, setMediaUploadOpen] = useState(false);
  
  // Horse attribution state
  const [allHorses, setAllHorses] = useState([]);
  const [cavalierHorses, setCavalierHorses] = useState([]);
  const [selectedHorseIds, setSelectedHorseIds] = useState([]);
  const [preferedHorseId, setPreferedHorseId] = useState(null);
  const [loadingHorses, setLoadingHorses] = useState(false);
  const [savingHorses, setSavingHorses] = useState(false);
  const [horseSearchTerm, setHorseSearchTerm] = useState('');
  
  // Approval modal state
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationInfo, setActivationInfo] = useState(null);
  const [approvalData, setApprovalData] = useState({
    role_club: 'cavalier_forfait',
    group_id: null,
    horse_id: null
  });
  const [allGroups, setAllGroups] = useState([]);
  const [approvingUser, setApprovingUser] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchAllHorses();
    fetchGroups();
  }, []);
  
  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groupes`);
      setAllGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const [allRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`),
        isAdmin() ? axios.get(`${API_URL}/api/users/pending`) : Promise.resolve({ data: [] })
      ]);
      setUsers(allRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllHorses = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/horses`);
      setAllHorses(res.data);
    } catch (error) {
      console.error('Error fetching horses:', error);
    }
  };

  const fetchCavalierHorses = async (userId) => {
    setLoadingHorses(true);
    try {
      const res = await axios.get(`${API_URL}/api/cavalier-profils/${userId}/chevaux`);
      setCavalierHorses(res.data.chevaux || []);
      // Get list of attributed horse IDs only (not DP or proprio)
      const attribuedIds = (res.data.chevaux || [])
        .filter(h => h.role === 'attribue')
        .map(h => h.id);
      setSelectedHorseIds(attribuedIds);
      setPreferedHorseId(res.data.cheval_prefere_id);
    } catch (error) {
      console.error('Error fetching cavalier horses:', error);
      setCavalierHorses([]);
      setSelectedHorseIds([]);
      setPreferedHorseId(null);
    } finally {
      setLoadingHorses(false);
    }
  };

  const handleSaveHorseAttribution = async () => {
    if (!selectedUser) return;
    
    setSavingHorses(true);
    try {
      await axios.put(`${API_URL}/api/cavalier-profils/${selectedUser.id}/chevaux`, {
        chevaux_attribues: selectedHorseIds,
        cheval_prefere_id: preferedHorseId
      });
      toast.success('Chevaux attribués avec succès');
      // Refresh the cavalier horses
      await fetchCavalierHorses(selectedUser.id);
    } catch (error) {
      console.error('Error saving horse attribution:', error);
      toast.error('Erreur lors de l\'attribution des chevaux');
    } finally {
      setSavingHorses(false);
    }
  };

  const toggleHorseSelection = (horseId) => {
    setSelectedHorseIds(prev => {
      if (prev.includes(horseId)) {
        // Remove horse - also remove as preferred if it was
        if (preferedHorseId === horseId) {
          setPreferedHorseId(null);
        }
        return prev.filter(id => id !== horseId);
      } else {
        return [...prev, horseId];
      }
    });
  };

  const handleSetPreferred = (horseId) => {
    setPreferedHorseId(prev => prev === horseId ? null : horseId);
  };

  // When selecting a user, fetch their horses
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setHorseSearchTerm('');
    if (user && user.role_club?.startsWith('cavalier')) {
      fetchCavalierHorses(user.id);
    } else {
      setCavalierHorses([]);
      setSelectedHorseIds([]);
      setPreferedHorseId(null);
    }
  };

  // Open approval modal with default values
  const handleApprove = (userId) => {
    const user = users.find(u => u.id === userId) || pendingUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setApprovalData({
        role_club: user.role_club || 'cavalier_forfait',
        group_id: null,
        horse_id: null
      });
      setApprovalModalOpen(true);
    }
  };

  // Complete approval with role, group and horse
  const handleCompleteApproval = async () => {
    if (!selectedUser) return;
    
    if (!approvalData.role_club) {
      toast.error('Veuillez sélectionner un rôle pour le cavalier');
      return;
    }
    
    setApprovingUser(true);
    try {
      // Call the approval endpoint with all data
      const response = await axios.post(`${API_URL}/api/users/${selectedUser.id}/approve-complete`, {
        role_club: approvalData.role_club,
        groupe_fixe_id: approvalData.group_id,
        horse_id: approvalData.horse_id
      });
      
      // Show success message with activation info
      const expiresAt = response.data.activation_expires_at;
      let expiresFormatted = '';
      if (expiresAt) {
        const expires = new Date(expiresAt);
        expiresFormatted = expires.toLocaleDateString('fr-FR', { 
          weekday: 'long', 
          day: 'numeric', 
          month: 'long',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Show activation sent modal
      setActivationInfo({
        user: selectedUser,
        email: selectedUser.email,
        expiresAt: expiresFormatted
      });
      setShowActivationModal(true);
      
      setApprovalModalOpen(false);
      setApprovalData({ role_club: 'cavalier_forfait', group_id: null, horse_id: null });
      fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de l\'approbation'));
    } finally {
      setApprovingUser(false);
    }
  };

  // Resend activation link
  const handleResendActivation = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/api/users/${userId}/resend-activation`);
      toast.success(response.data.message || 'Lien d\'activation renvoyé');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'envoi du lien'));
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/users/${userId}/reject`, { reason: '' });
      toast.success('Utilisateur refusé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors du refus');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'actif' ? 'inactif' : 'actif';
      await axios.post(`${API_URL}/api/users/${userId}/status`, { statut: newStatus });
      toast.success(`Utilisateur ${newStatus === 'actif' ? 'activé' : 'désactivé'}`);
      // Update the selected user's status locally
      setSelectedUser(prev => prev ? { ...prev, statut: newStatus } : null);
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleUpdateApproval = async (userId, approvalStatus) => {
    try {
      // Use the dedicated endpoints for approval/rejection
      if (approvalStatus === 'approuve') {
        await axios.post(`${API_URL}/api/users/${userId}/approve`);
        toast.success('Utilisateur approuvé');
      } else if (approvalStatus === 'refuse' || approvalStatus === 'rejete') {
        await axios.post(`${API_URL}/api/users/${userId}/reject`);
        toast.success('Utilisateur révoqué');
      }
      // Update the selected user locally
      const newActivityStatus = approvalStatus === 'approuve' ? 'actif' : 'inactif';
      setSelectedUser(prev => prev ? { ...prev, is_approved: approvalStatus, statut: newActivityStatus } : null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating approval:', error.response?.data || error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setDeletingUser(true);
    try {
      await axios.delete(`${API_URL}/api/users/${selectedUser.id}`);
      toast.success('Utilisateur supprimé avec succès');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or simple string errors
      const detail = error.response?.data?.detail;
      let errorMsg = 'Erreur lors de la suppression';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setDeletingUser(false);
    }
  };

  // Check if user can be deleted (inactive AND rejected)
  const canDeleteUser = (user) => {
    if (!user) return false;
    const isInactive = user.statut === 'inactif';
    const isRejected = ['refuse', 'rejete'].includes(user.is_approved);
    return isInactive && isRejected;
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}`, { role_club: newRole });
      toast.success(`Rôle modifié en ${roleLabels[newRole]}`);
      // Update the selected user locally
      setSelectedUser(prev => prev ? { ...prev, role_club: newRole } : null);
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la modification du rôle');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.pseudo?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by status
    let matchesStatus = false;
    if (statusFilter === 'tous') {
      matchesStatus = true;
    } else if (statusFilter === 'activation_pending') {
      matchesStatus = user.activation_pending === true;
    } else {
      matchesStatus = user.statut === statusFilter && !user.activation_pending;
    }
    
    if (!matchesStatus) return false;
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'cavaliers') return matchesSearch && user.role_club?.startsWith('cavalier');
    if (activeTab === 'instructeurs') return matchesSearch && user.role_club?.startsWith('instructeur');
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D]">Cavaliers</h1>
          <p className="text-stone-500 mt-1">
            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} 
            {statusFilter === 'activation_pending' && ' en attente d\'activation'}
            {statusFilter === 'actif' && ` actif${filteredUsers.length > 1 ? 's' : ''}`}
            {statusFilter === 'inactif' && ` suspendu${filteredUsers.length > 1 ? 's' : ''}`}
            {filteredUsers.length !== users.length && ` sur ${users.length}`}
          </p>
        </div>
      </div>

      {/* Pending approvals alert */}
      {isAdmin() && pendingUsers.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              {pendingUsers.length} demande(s) en attente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingUsers.slice(0, 3).map(user => (
              <UserCard 
                key={user.id} 
                user={user} 
                showActions 
                isAdmin={isAdmin()}
                onSelect={handleSelectUser}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Rechercher un cavalier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 input-field"
            data-testid="search-users"
          />
        </div>
        
        {/* Status filter */}
        <div className="flex items-center gap-2 bg-stone-100 rounded-lg p-1">
          <button
            onClick={() => setStatusFilter('actif')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              statusFilter === 'actif' 
                ? 'bg-white text-green-700 shadow-sm' 
                : 'text-stone-600 hover:text-stone-800'
            }`}
            data-testid="filter-actif"
          >
            <UserCheck className="w-4 h-4" />
            Actifs
          </button>
          <button
            onClick={() => setStatusFilter('inactif')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              statusFilter === 'inactif' 
                ? 'bg-white text-red-700 shadow-sm' 
                : 'text-stone-600 hover:text-stone-800'
            }`}
            data-testid="filter-inactif"
          >
            <UserX className="w-4 h-4" />
            Suspendus
          </button>
          <button
            onClick={() => setStatusFilter('activation_pending')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              statusFilter === 'activation_pending' 
                ? 'bg-white text-amber-700 shadow-sm' 
                : 'text-stone-600 hover:text-stone-800'
            }`}
            data-testid="filter-activation-pending"
          >
            <Clock className="w-4 h-4" />
            À activer
          </button>
          <button
            onClick={() => setStatusFilter('tous')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              statusFilter === 'tous' 
                ? 'bg-white text-stone-700 shadow-sm' 
                : 'text-stone-600 hover:text-stone-800'
            }`}
            data-testid="filter-tous"
          >
            Tous
          </button>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">Tous</TabsTrigger>
            <TabsTrigger value="cavaliers">Cavaliers</TabsTrigger>
            <TabsTrigger value="instructeurs">Instructeurs</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Users grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredUsers.length === 0 ? (
          <div className="col-span-full">
            <div className="empty-state">
              <Search className="empty-state-icon" />
              <h3 className="empty-state-title">Aucun résultat</h3>
              <p className="empty-state-description">Aucun utilisateur ne correspond à votre recherche</p>
            </div>
          </div>
        ) : (
          filteredUsers.map(user => (
            <UserCard 
              key={user.id} 
              user={user}
              isAdmin={isAdmin()}
              onSelect={handleSelectUser}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))
        )}
      </div>

      {/* User detail modal */}
      {selectedUser && (
        <Dialog open={!!selectedUser} onOpenChange={() => handleSelectUser(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Détail du cavalier</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center text-2xl font-medium">
                  {selectedUser.prenom?.[0]}{selectedUser.nom?.[0]}
                </div>
                <div>
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    {selectedUser.prenom} {selectedUser.nom}
                    {selectedUser.pseudo && <span className="font-normal text-stone-500"> ({selectedUser.pseudo})</span>}
                    {selectedUser.is_mineur && (
                      <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">
                        Mineur
                      </span>
                    )}
                  </h3>
                  <Badge className={roleColors[selectedUser.role_club]}>
                    {roleLabels[selectedUser.role_club]}
                  </Badge>
                </div>
              </div>
              
              <div className="space-y-3 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium text-stone-700">Informations personnelles</h4>
                  {isAdmin() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUser({...selectedUser, _editing: !selectedUser._editing})}
                    >
                      {selectedUser._editing ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
                
                {selectedUser._editing && isAdmin() ? (
                  <div className="space-y-3 bg-stone-50 p-3 rounded-lg">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Titre</Label>
                        <Select
                          value={selectedUser.titre || ''}
                          onValueChange={(v) => setSelectedUser({...selectedUser, titre: v})}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Titre" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M.">M.</SelectItem>
                            <SelectItem value="Mme">Mme</SelectItem>
                            <SelectItem value="Mlle">Mlle</SelectItem>
                            <SelectItem value="Dr">Dr</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Prénom</Label>
                        <Input
                          value={selectedUser.prenom || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, prenom: e.target.value})}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Nom</Label>
                        <Input
                          value={selectedUser.nom || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, nom: e.target.value})}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Téléphone</Label>
                      <Input
                        value={selectedUser.telephone || ''}
                        onChange={(e) => setSelectedUser({...selectedUser, telephone: e.target.value})}
                        className="h-8 text-sm"
                        placeholder="0612345678"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Date de naissance <span className="text-rose-500">*</span></Label>
                      <Input
                        type="date"
                        value={selectedUser.date_naissance || ''}
                        onChange={(e) => setSelectedUser({...selectedUser, date_naissance: e.target.value})}
                        className={`h-8 text-sm ${!selectedUser.date_naissance ? 'border-rose-300' : ''}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Adresse (rue)</Label>
                      <Input
                        value={selectedUser.adresse_rue || selectedUser.adresse || ''}
                        onChange={(e) => setSelectedUser({...selectedUser, adresse_rue: e.target.value, adresse: e.target.value})}
                        className="h-8 text-sm"
                        placeholder="123 rue des Écuries"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Code postal</Label>
                        <Input
                          value={selectedUser.adresse_cp || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, adresse_cp: e.target.value})}
                          className="h-8 text-sm"
                          placeholder="75001"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Ville</Label>
                        <Input
                          value={selectedUser.adresse_ville || ''}
                          onChange={(e) => setSelectedUser({...selectedUser, adresse_ville: e.target.value})}
                          className="h-8 text-sm"
                          placeholder="Paris"
                        />
                      </div>
                    </div>
                    
                    {/* Niveau galop - uniquement pour les cavaliers */}
                    {selectedUser.role_club?.startsWith('cavalier') && (
                      <div>
                        <Label className="text-xs">Niveau Galop</Label>
                        <Select
                          value={selectedUser.niveau_galop?.toString() || 'none'}
                          onValueChange={(v) => setSelectedUser({...selectedUser, niveau_galop: v === 'none' ? null : parseInt(v)})}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Non spécifié" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Non spécifié</SelectItem>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(niveau => (
                              <SelectItem key={niveau} value={niveau.toString()}>
                                Galop {niveau}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <Button
                      size="sm"
                      className="w-full mt-2"
                      onClick={async () => {
                        try {
                          await axios.put(`${API_URL}/api/users/${selectedUser.id}`, {
                            titre: selectedUser.titre,
                            prenom: selectedUser.prenom,
                            nom: selectedUser.nom,
                            telephone: selectedUser.telephone,
                            date_naissance: selectedUser.date_naissance,
                            adresse: selectedUser.adresse_rue || selectedUser.adresse,
                            adresse_rue: selectedUser.adresse_rue || selectedUser.adresse,
                            adresse_cp: selectedUser.adresse_cp,
                            adresse_ville: selectedUser.adresse_ville,
                            niveau_galop: selectedUser.niveau_galop
                          });
                          toast.success('Informations mises à jour');
                          setSelectedUser({...selectedUser, _editing: false});
                          fetchUsers();
                        } catch (error) {
                          toast.error('Erreur lors de la mise à jour');
                        }
                      }}
                    >
                      Enregistrer
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Alerte champs obligatoires manquants */}
                    {(() => {
                      const missingFields = [];
                      if (!selectedUser.date_naissance) missingFields.push('Date de naissance');
                      if (!selectedUser.pseudo) missingFields.push('Pseudo');
                      if (!selectedUser.adresse_rue && !selectedUser.adresse) missingFields.push('Adresse');
                      if (!selectedUser.adresse_cp) missingFields.push('Code postal');
                      if (!selectedUser.adresse_ville) missingFields.push('Ville');
                      
                      if (missingFields.length > 0) {
                        return (
                          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-rose-700">Informations obligatoires manquantes</p>
                                <ul className="mt-1 text-sm text-rose-600 list-disc list-inside">
                                  {missingFields.map((field, idx) => (
                                    <li key={idx}>{field}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    <div className="flex items-center gap-3 text-stone-600">
                      <Mail className="w-5 h-5" />
                      <span>{selectedUser.email}</span>
                    </div>
                    
                    {selectedUser.telephone && (
                      <div className="flex items-center gap-3 text-stone-600">
                        <Phone className="w-5 h-5" />
                        <span>{selectedUser.telephone}</span>
                      </div>
                    )}
                    
                    {/* Pseudo */}
                    <div className="flex items-center gap-3 text-stone-600">
                      <User className="w-5 h-5" />
                      <span>
                        {selectedUser.pseudo 
                          ? selectedUser.pseudo
                          : <span className="text-rose-500 italic">Pseudo non renseigné</span>
                        }
                      </span>
                    </div>
                    
                    {/* Date de naissance */}
                    <div className="flex items-center gap-3 text-stone-600">
                      <Calendar className="w-5 h-5" />
                      <span>
                        {selectedUser.date_naissance 
                          ? new Date(selectedUser.date_naissance).toLocaleDateString('fr-FR')
                          : <span className="text-rose-500 italic">Date de naissance non renseignée</span>
                        }
                      </span>
                    </div>
                    
                    {/* Adresse - toujours affichée */}
                    <div className="flex items-center gap-3 text-stone-600">
                      <MapPin className="w-5 h-5" />
                      <div className="text-sm">
                        {(selectedUser.adresse_rue || selectedUser.adresse) ? (
                          <>
                            <div>{selectedUser.adresse_rue || selectedUser.adresse}</div>
                            <div className="text-stone-500">
                              {selectedUser.adresse_cp || <span className="text-rose-500 italic">CP ?</span>}
                              {' '}
                              {selectedUser.adresse_ville || <span className="text-rose-500 italic">Ville ?</span>}
                            </div>
                          </>
                        ) : (
                          <span className="text-rose-500 italic">Adresse non renseignée</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Niveau galop pour les cavaliers */}
                    {selectedUser.role_club?.startsWith('cavalier') && (
                      <div className="flex items-center gap-3 text-stone-600">
                        <Award className="w-5 h-5" />
                        <span>
                          {selectedUser.niveau_galop 
                            ? `Galop ${selectedUser.niveau_galop}` 
                            : <span className="text-stone-400 italic">Niveau galop non renseigné</span>
                          }
                        </span>
                      </div>
                    )}
                    
                    {/* Section Mineur - Parent/Tuteur */}
                    {selectedUser.is_mineur && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Baby className="w-5 h-5 text-amber-600" />
                          <span className="font-medium text-amber-800">Cavalier mineur</span>
                        </div>
                        
                        {selectedUser.parent_info ? (
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <UsersIcon className="w-4 h-4 text-amber-600" />
                              <span className="text-amber-900 font-medium">
                                {selectedUser.parent_info.prenom} {selectedUser.parent_info.nom}
                              </span>
                              <span className="text-amber-600 text-xs">
                                ({selectedUser.parent_info.lien === 'parent' ? 'Parent' : 
                                  selectedUser.parent_info.lien === 'tuteur' ? 'Tuteur légal' : 'Responsable'})
                              </span>
                            </div>
                            
                            {selectedUser.parent_info.telephone && (
                              <div className="flex items-center gap-2 text-amber-800">
                                <Phone className="w-4 h-4 text-amber-500" />
                                <a href={`tel:${selectedUser.parent_info.telephone}`} className="hover:underline">
                                  {selectedUser.parent_info.telephone}
                                </a>
                              </div>
                            )}
                            
                            {selectedUser.parent_info.email && (
                              <div className="flex items-center gap-2 text-amber-800">
                                <Mail className="w-4 h-4 text-amber-500" />
                                <a href={`mailto:${selectedUser.parent_info.email}`} className="hover:underline">
                                  {selectedUser.parent_info.email}
                                </a>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-xs text-amber-600 mt-2 pt-2 border-t border-amber-200">
                              <CheckCircle className="w-3 h-3" />
                              <span>Autorisation parentale validée</span>
                              {selectedUser.notifications_parent && (
                                <span className="ml-2 px-2 py-0.5 bg-amber-100 rounded text-amber-700">
                                  Notif: {selectedUser.notifications_parent === 'both' ? 'Parent + Enfant' : 
                                          selectedUser.notifications_parent === 'parent_only' ? 'Parent seul' : 'Enfant seul'}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-amber-700 text-sm italic">
                            Informations du parent/tuteur non renseignées
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* ===== SECTION GALERIE ===== */}
              {selectedUser.role_club?.startsWith('cavalier') && (
                <div className="pt-4 border-t space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-stone-700 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Galerie photos/vidéos
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMediaUploadOpen(true)}
                      className="text-[#2E4A3D] border-[#2E4A3D]/30 hover:bg-[#2E4A3D]/5"
                      data-testid="add-media-cavalier-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                  <p className="text-xs text-stone-500">
                    Les médias ajoutés ici seront automatiquement tagués avec ce cavalier.
                  </p>
                </div>
              )}
              
              {/* ===== SECTION 3: GESTION ADMINISTRATIVE ===== */}
              {isAdmin() && (
                <div className="pt-4 border-t space-y-4">
                  <h4 className="font-medium text-stone-700 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Gestion administrative
                  </h4>
                  
                  {/* Role management */}
                  <div className="space-y-2">
                    <Label className="text-sm text-stone-600">Rôle au club</Label>
                    <Select 
                      value={selectedUser.role_club} 
                      onValueChange={(value) => handleUpdateRole(selectedUser.id, value)}
                    >
                      <SelectTrigger data-testid="role-select">
                        <SelectValue placeholder="Sélectionner un rôle" />
                      </SelectTrigger>
                      <SelectContent>
                        {allRoles.map(role => (
                          <SelectItem key={role.value} value={role.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{role.label}</span>
                              <span className="text-xs text-stone-500">{role.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-stone-400">
                      {allRoles.find(r => r.value === selectedUser.role_club)?.description}
                    </p>
                  </div>
                  
                  {/* Approval controls */}
                  <div className="space-y-2">
                    <Label className="text-sm text-stone-600">Statut d&apos;approbation</Label>
                    {selectedUser.is_approved === 'en_attente' ? (
                      <div className="flex gap-2">
                        <Button
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleApprove(selectedUser.id)}
                          data-testid="modal-approve-btn"
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Approuver
                        </Button>
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleUpdateApproval(selectedUser.id, 'refuse')}
                          data-testid="modal-reject-btn"
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    ) : selectedUser.is_approved === 'refuse' ? (
                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleApprove(selectedUser.id)}
                        data-testid="modal-reapprove-btn"
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Approuver
                      </Button>
                    ) : (
                      <div className="flex gap-2 items-center">
                        <Badge className="bg-green-100 text-green-800 py-1 px-3">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Approuvé
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleUpdateApproval(selectedUser.id, 'refuse')}
                          data-testid="modal-revoke-btn"
                        >
                          Révoquer
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Resend activation link - for approved users without password */}
                  {selectedUser.is_approved === 'approuve' && selectedUser.activation_pending && (
                    <div className="space-y-2">
                      <Label className="text-sm text-stone-600">Activation en attente</Label>
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800 mb-3">
                          Ce cavalier n'a pas encore activé son compte.
                        </p>
                        <Button
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => handleResendActivation(selectedUser.id)}
                          data-testid="resend-activation-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Renvoyer le lien d'activation
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Activity toggle - only for approved AND activated users */}
                  {selectedUser.is_approved === 'approuve' && !selectedUser.activation_pending && (
                    <div className="space-y-2">
                      <Label className="text-sm text-stone-600">Accès au compte</Label>
                      <Button
                        variant={selectedUser.statut === 'actif' ? 'destructive' : 'default'}
                        className={`w-full ${selectedUser.statut !== 'actif' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                        onClick={() => handleToggleStatus(selectedUser.id, selectedUser.statut)}
                        data-testid="modal-toggle-status-btn"
                      >
                        <Power className="w-4 h-4 mr-2" />
                        {selectedUser.statut === 'actif' ? 'Suspendre l\'accès' : 'Réactiver l\'accès'}
                      </Button>
                      <p className="text-xs text-stone-500">
                        {selectedUser.statut === 'actif' 
                          ? 'L\'utilisateur pourra toujours se connecter.' 
                          : 'L\'utilisateur ne peut plus se connecter.'}
                      </p>
                    </div>
                  )}
                  
                  {/* Delete user - only for inactive AND rejected users */}
                  {canDeleteUser(selectedUser) && (
                    <div className="space-y-2 pt-2 border-t border-red-200">
                      <Button
                        variant="destructive"
                        className="w-full bg-red-600 hover:bg-red-700"
                        onClick={() => setDeleteDialogOpen(true)}
                        data-testid="delete-user-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer définitivement
                      </Button>
                      <p className="text-xs text-stone-500">
                        Cette action est irréversible.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ===== SECTION 4: FORMULE DE FORFAIT ===== */}
              {/* Only for cavalier_forfait */}
              {selectedUser.role_club === 'cavalier_forfait' && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
                          <div className="flex items-center gap-2">
                            <Package className="w-5 h-5 text-green-600" />
                            <Label className="font-medium text-green-800">Formule de forfait</Label>
                          </div>
                          
                          {/* Calculate age and show appropriate options */}
                          {(() => {
                            const birthDate = selectedUser.date_naissance;
                            let age = null;
                            let isMinor = null;
                            
                            if (birthDate) {
                              try {
                                const birth = new Date(birthDate);
                                const today = new Date();
                                age = today.getFullYear() - birth.getFullYear();
                                const monthDiff = today.getMonth() - birth.getMonth();
                                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                                  age--;
                                }
                                isMinor = age < 18;
                              } catch (e) {
                                console.error('Error calculating age:', e);
                              }
                            }
                            
                            return (
                              <div className="space-y-2">
                                {age !== null && (
                                  <p className="text-sm text-green-700">
                                    Âge du cavalier : <strong>{age} ans</strong> 
                                    <Badge className="ml-2 bg-green-100 text-green-800 text-xs">
                                      {isMinor ? 'Moins de 18 ans' : '18 ans et plus'}
                                    </Badge>
                                  </p>
                                )}
                                {!birthDate && (
                                  <p className="text-sm text-amber-600">
                                    ⚠️ Date de naissance non renseignée - veuillez la compléter pour calculer le tarif applicable.
                                  </p>
                                )}
                                
                                <div className="grid grid-cols-2 gap-3 mt-2">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.put(`${API_URL}/api/users/${selectedUser.id}`, { forfait_formula: '1_cours' });
                                        // Update selectedUser with server response
                                        setSelectedUser(response.data);
                                        // Refresh users list from server
                                        fetchUsers();
                                        toast.success('Forfait 1 cours/semaine enregistré');
                                      } catch (e) {
                                        toast.error('Erreur lors de la mise à jour');
                                      }
                                    }}
                                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                                      selectedUser.forfait_formula === '1_cours'
                                        ? 'border-green-600 bg-green-100 ring-2 ring-green-500'
                                        : 'border-stone-200 hover:border-green-300 bg-white'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {selectedUser.forfait_formula === '1_cours' && (
                                        <span className="text-green-600 font-bold">✓</span>
                                      )}
                                      <span className="font-medium text-stone-800">1 cours / semaine</span>
                                    </div>
                                    <div className="text-xs text-stone-500 mt-1">
                                      {isMinor === true && 'Tarif moins de 18 ans'}
                                      {isMinor === false && 'Tarif 18 ans et plus'}
                                      {isMinor === null && 'Tarif selon âge'}
                                    </div>
                                  </button>
                                  
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        const response = await axios.put(`${API_URL}/api/users/${selectedUser.id}`, { forfait_formula: '2_cours' });
                                        // Update selectedUser with server response
                                        setSelectedUser(response.data);
                                        // Refresh users list from server
                                        fetchUsers();
                                        toast.success('Forfait 2 cours/semaine enregistré');
                                      } catch (e) {
                                        toast.error('Erreur lors de la mise à jour');
                                      }
                                    }}
                                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                                      selectedUser.forfait_formula === '2_cours'
                                        ? 'border-green-600 bg-green-100 ring-2 ring-green-500'
                                        : 'border-stone-200 hover:border-green-300 bg-white'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {selectedUser.forfait_formula === '2_cours' && (
                                        <span className="text-green-600 font-bold">✓</span>
                                      )}
                                      <span className="font-medium text-stone-800">2 cours / semaine</span>
                                    </div>
                                    <div className="text-xs text-stone-500 mt-1">
                                      {isMinor === true && 'Tarif moins de 18 ans'}
                                      {isMinor === false && 'Tarif 18 ans et plus'}
                                      {isMinor === null && 'Tarif selon âge'}
                                    </div>
                                  </button>
                                </div>
                                
                                {!selectedUser.forfait_formula && (
                                  <p className="text-sm text-amber-600 mt-2">
                                    ⚠️ Veuillez sélectionner une formule de forfait.
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
              )}

              {/* ===== SECTION 5: CHEVAUX ASSOCIÉS ===== */}
              {/* Only for cavaliers (admins can see) */}
              {isAdmin() && selectedUser.role_club?.startsWith('cavalier') && (
                <div className="pt-4 border-t space-y-4">
                  <h4 className="font-medium text-stone-700 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Chevaux associés
                  </h4>
                  
                  {loadingHorses ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-[#2E4A3D]" />
                    </div>
                  ) : (
                    <>
                      {/* Info message about role-specific rules */}
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-800">
                          {selectedUser.role_club === 'cavalier_proprio' && (
                            <>Un <strong>propriétaire</strong> est lié à son cheval via la fiche du cheval (onglet Cavaliers).</>
                          )}
                          {selectedUser.role_club === 'cavalier_pp' && (
                            <>Un <strong>cavalier PP</strong> peut être associé uniquement à un cheval de propriétaire ou "inconnu".</>
                          )}
                          {(selectedUser.role_club === 'cavalier_dp' || selectedUser.role_club === 'cavalier_forfait') && (
                            <>Un <strong>{selectedUser.role_club === 'cavalier_dp' ? 'demi-pensionnaire' : 'cavalier forfait'}</strong> peut être associé uniquement à un cheval du club.</>
                          )}
                        </p>
                      </div>
                      
                      {/* Currently linked horses (read-only display) */}
                      {cavalierHorses.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm text-stone-500">Chevaux actuellement liés :</Label>
                          <div className="space-y-2">
                            {cavalierHorses.map(horse => (
                              <div 
                                key={horse.id} 
                                className="flex items-center justify-between p-2 bg-stone-50 rounded-lg border"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-stone-800">{horse.nom}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {horse.role === 'proprietaire' ? 'Propriétaire' : 
                                     horse.role === 'proprietaire_partage' ? 'Prop. Partagé' : 
                                     horse.role === 'demi_pensionnaire' ? 'Demi-pension' : 'Attribué'}
                                  </Badge>
                                </div>
                                <button
                                  onClick={() => handleSetPreferred(horse.id)}
                                  className={`p-1 rounded transition-colors ${
                                    horse.is_prefere || preferedHorseId === horse.id
                                      ? 'text-amber-500' 
                                      : 'text-stone-300 hover:text-amber-400'
                                  }`}
                                  title={horse.is_prefere || preferedHorseId === horse.id ? 'Cheval préféré' : 'Définir comme préféré'}
                                >
                                  <Star className={`w-5 h-5 ${horse.is_prefere || preferedHorseId === horse.id ? 'fill-current' : ''}`} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Attribution UI for PP, DP, FF (not for proprio - that's done in horse profile) */}
                      {['cavalier_pp', 'cavalier_dp', 'cavalier_forfait'].includes(selectedUser.role_club) && (
                        <div className="space-y-3">
                          {/* Show maximum 2 horses rule info */}
                          {cavalierHorses.length >= 2 ? (
                            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <p className="text-sm text-amber-800">
                                <strong>Limite atteinte :</strong> Ce cavalier est déjà associé à 2 chevaux (maximum autorisé pour les PP/DP/FF).
                              </p>
                            </div>
                          ) : (
                            <>
                              <Label className="text-sm text-stone-600 font-medium">
                                {selectedUser.role_club === 'cavalier_pp' 
                                  ? 'Attribuer à un cheval de propriétaire :' 
                                  : 'Attribuer à un cheval du club :'}
                                <span className="text-xs text-stone-400 ml-2">
                                  ({cavalierHorses.length}/2 chevaux)
                                </span>
                              </Label>
                          
                              {/* Filter horses based on role */}
                              {(() => {
                                const eligibleHorses = allHorses.filter(h => {
                                  // Exclude deleted horses
                                  if (h.deleted || h.statut !== 'actif') return false;
                                  
                                  // Already linked to this user
                                  const alreadyLinked = cavalierHorses.some(ch => ch.id === h.id);
                                  if (alreadyLinked) return false;
                                  
                                  if (selectedUser.role_club === 'cavalier_pp') {
                                    // PP can only be assigned to horses with owner OR unknown (not club horses)
                                    return !h.is_club_horse;
                                  } else {
                                    // DP/FF can only be assigned to club horses
                                    return h.is_club_horse === true;
                                  }
                                });
                                
                                if (eligibleHorses.length === 0) {
                                  return (
                                    <p className="text-sm text-stone-400 italic py-2">
                                      {selectedUser.role_club === 'cavalier_pp' 
                                        ? 'Aucun cheval de propriétaire disponible' 
                                        : 'Aucun cheval du club disponible'}
                                    </p>
                                  );
                                }
                                
                                return (
                                  <div className="space-y-2">
                                    <Select
                                      value=""
                                      onValueChange={async (horseId) => {
                                        if (!horseId) return;
                                        
                                        // Check if user already has 2 horses (client-side validation)
                                        if (cavalierHorses.length >= 2) {
                                          toast.error('Ce cavalier est déjà associé à 2 chevaux (maximum autorisé)');
                                          return;
                                        }
                                        
                                        try {
                                          const horse = allHorses.find(h => h.id === horseId);
                                          if (!horse) return;
                                          
                                          // Determine which field to update based on role
                                          let updateData = {};
                                          if (selectedUser.role_club === 'cavalier_pp') {
                                            // Add user to proprietaires_partages
                                            const currentPP = horse.proprietaires_partages || [];
                                            updateData = { proprietaires_partages: [...currentPP, selectedUser.id] };
                                          } else {
                                            // Add user to demi_pensionnaires (for DP and FF)
                                            const currentDP = horse.demi_pensionnaires || [];
                                            updateData = { demi_pensionnaires: [...currentDP, selectedUser.id] };
                                          }
                                          
                                          await axios.put(`${API_URL}/api/horses/${horseId}`, updateData);
                                          toast.success(`${horse.nom} attribué avec succès`);
                                          
                                          // Refresh cavalier horses
                                          fetchCavalierHorses(selectedUser.id);
                                          // Refresh all horses
                                          const res = await axios.get(`${API_URL}/api/horses`);
                                          setAllHorses(res.data);
                                        } catch (error) {
                                          console.error('Error assigning horse:', error);
                                          const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'attribution';
                                          toast.error(errorMsg);
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="w-full bg-white">
                                        <SelectValue placeholder="Sélectionner un cheval..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {eligibleHorses.map(horse => (
                                          <SelectItem key={horse.id} value={horse.id}>
                                            <div className="flex items-center gap-2">
                                              <span>{horse.nom}</span>
                                              {horse.proprietaire_id && (
                                                <span className="text-xs text-stone-400">
                                                  (Proprio: {allHorses.find(h => h.id === horse.id)?.proprietaire_nom || 'inconnu'})
                                                </span>
                                              )}
                                              {!horse.proprietaire_id && !horse.is_club_horse && (
                                                <span className="text-xs text-stone-400">(Proprio inconnu)</span>
                                              )}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                
                                {/* Remove horse button for each linked horse (except proprietaire role) */}
                                {cavalierHorses.filter(h => h.role === 'proprietaire_partage' || h.role === 'demi_pensionnaire').length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    <Label className="text-xs text-stone-400">Retirer un cheval :</Label>
                                    {cavalierHorses
                                      .filter(h => h.role === 'proprietaire_partage' || h.role === 'demi_pensionnaire')
                                      .map(horse => (
                                        <div key={horse.id} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-100">
                                          <span className="text-sm text-stone-700">{horse.nom}</span>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-100 h-7 px-2"
                                            onClick={async () => {
                                              try {
                                                const fullHorse = allHorses.find(h => h.id === horse.id);
                                                if (!fullHorse) return;
                                                
                                                let updateData = {};
                                                if (horse.role === 'proprietaire_partage') {
                                                  const newPP = (fullHorse.proprietaires_partages || []).filter(id => id !== selectedUser.id);
                                                  updateData = { proprietaires_partages: newPP };
                                                } else {
                                                  const newDP = (fullHorse.demi_pensionnaires || []).filter(id => id !== selectedUser.id);
                                                  updateData = { demi_pensionnaires: newDP };
                                                }
                                                
                                                await axios.put(`${API_URL}/api/horses/${horse.id}`, updateData);
                                                toast.success(`${horse.nom} retiré`);
                                                fetchCavalierHorses(selectedUser.id);
                                                const res = await axios.get(`${API_URL}/api/horses`);
                                                setAllHorses(res.data);
                                              } catch (error) {
                                                console.error('Error removing horse:', error);
                                                toast.error('Erreur lors du retrait');
                                              }
                                            }}
                                          >
                                            <X className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* For proprio - show info only, attribution is in horse profile */}
                      {selectedUser.role_club === 'cavalier_proprio' && cavalierHorses.length === 0 && (
                        <p className="text-sm text-stone-400 italic">
                          Aucun cheval attribué. L'attribution se fait via la fiche du cheval.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Media Upload Modal for cavalier */}
      {selectedUser && (
        <UploadMediaModal
          open={mediaUploadOpen}
          onClose={() => setMediaUploadOpen(false)}
          onSuccess={() => {
            setMediaUploadOpen(false);
            toast.success('Médias ajoutés au cavalier');
          }}
          context={{ type: 'cavalier', cavalierId: selectedUser.id }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer définitivement{' '}
              <span className="font-semibold">
                {selectedUser?.prenom} {selectedUser?.nom}
              </span>
              {' '}({selectedUser?.email}).
              <br /><br />
              Cette action est <span className="font-semibold text-red-600">irréversible</span> et libérera 
              son identifiant et mot de passe pour une éventuelle réutilisation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingUser}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deletingUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deletingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approval Modal with role, group, and horse selection */}
      <Dialog open={approvalModalOpen} onOpenChange={setApprovalModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Programmer l'approbation
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6">
              {/* User info */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center font-medium">
                    {selectedUser.pseudo?.substring(0, 2).toUpperCase() || 
                     `${selectedUser.prenom?.[0]}${selectedUser.nom?.[0]}`}
                  </div>
                  <div>
                    <p className="font-medium text-stone-800">
                      {selectedUser.pseudo || `${selectedUser.prenom} ${selectedUser.nom}`}
                    </p>
                    <p className="text-sm text-stone-500">{selectedUser.email}</p>
                  </div>
                </div>
              </div>

              {/* Role selection - MANDATORY */}
              <div className="space-y-2">
                <Label className="font-medium text-stone-700">
                  Rôle au club <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={approvalData.role_club}
                  onValueChange={(v) => setApprovalData({...approvalData, role_club: v, horse_id: null})}
                >
                  <SelectTrigger data-testid="approval-role-select">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    {allRoles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-stone-500">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Group selection - OPTIONAL */}
              <div className="space-y-2">
                <Label className="font-medium text-stone-700">
                  Groupe de cavaliers <span className="text-stone-400 text-sm">(optionnel)</span>
                </Label>
                <Select 
                  value={approvalData.group_id || 'none'}
                  onValueChange={(v) => setApprovalData({...approvalData, group_id: v === 'none' ? null : v})}
                >
                  <SelectTrigger data-testid="approval-group-select">
                    <SelectValue placeholder="Aucun groupe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun groupe</SelectItem>
                    {allGroups.map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500">
                  Vous pourrez modifier le groupe ultérieurement.
                </p>
              </div>

              {/* Horse selection - OPTIONAL, only for proprio/PP/DP roles */}
              {['cavalier_proprio', 'cavalier_pp', 'cavalier_dp'].includes(approvalData.role_club) && (
                <div className="space-y-2">
                  <Label className="font-medium text-stone-700">
                    Cheval attribué <span className="text-stone-400 text-sm">(optionnel)</span>
                  </Label>
                  <Select 
                    value={approvalData.horse_id || 'none'}
                    onValueChange={(v) => setApprovalData({...approvalData, horse_id: v === 'none' ? null : v})}
                  >
                    <SelectTrigger data-testid="approval-horse-select">
                      <SelectValue placeholder="Aucun cheval" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun cheval (à définir plus tard)</SelectItem>
                      {allHorses
                        .filter(h => {
                          if (approvalData.role_club === 'cavalier_proprio') {
                            // For proprio: show horses without owner
                            return !h.proprietaire_id && h.statut === 'actif' && !h.est_club;
                          } else if (approvalData.role_club === 'cavalier_pp') {
                            // For PP: show horses with owner (to be added as shared rider)
                            return h.proprietaire_id && h.statut === 'actif';
                          } else if (approvalData.role_club === 'cavalier_dp') {
                            // For DP: show club horses
                            return (h.est_club || !h.proprietaire_id) && h.statut === 'actif';
                          }
                          return false;
                        })
                        .map(horse => (
                          <SelectItem key={horse.id} value={horse.id}>
                            {horse.nom}
                            {horse.proprietaire_id && ' (cheval de propriétaire)'}
                            {horse.est_club && ' (cheval du club)'}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500">
                    {approvalData.role_club === 'cavalier_proprio' && 
                      "Le cavalier sera propriétaire de ce cheval et payera la pension."}
                    {approvalData.role_club === 'cavalier_pp' && 
                      "Le cavalier partagera ce cheval avec son propriétaire (pas de pension)."}
                    {approvalData.role_club === 'cavalier_dp' && 
                      "Le cavalier sera demi-pensionnaire sur ce cheval du club."}
                  </p>
                </div>
              )}

              {/* Summary */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">Récapitulatif</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• <strong>Rôle :</strong> {allRoles.find(r => r.value === approvalData.role_club)?.label}</li>
                  <li>• <strong>Groupe :</strong> {approvalData.group_id 
                    ? allGroups.find(g => g.id === approvalData.group_id)?.nom 
                    : 'Aucun'}</li>
                  {['cavalier_proprio', 'cavalier_pp', 'cavalier_dp'].includes(approvalData.role_club) && (
                    <li>• <strong>Cheval :</strong> {approvalData.horse_id 
                      ? allHorses.find(h => h.id === approvalData.horse_id)?.nom 
                      : 'À définir plus tard'}</li>
                  )}
                </ul>
                <p className="text-xs text-green-600 mt-2 italic">
                  Une notification sera envoyée au cavalier.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setApprovalModalOpen(false)}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleCompleteApproval}
                  disabled={approvingUser || !approvalData.role_club}
                  data-testid="confirm-approval-btn"
                >
                  {approvingUser ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <UserCheck className="w-4 h-4 mr-2" />
                  )}
                  Approuver
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Temporary Password Modal */}
      {/* Activation Link Sent Modal */}
      <Dialog open={showActivationModal} onOpenChange={setShowActivationModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-green-700 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Lien d'activation envoyé !
            </DialogTitle>
          </DialogHeader>
          
          {activationInfo && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 mb-2">
                  <strong>{activationInfo.user.prenom} {activationInfo.user.nom}</strong> a été approuvé avec succès.
                </p>
                <p className="text-sm text-green-700">
                  Un email avec le lien d'activation a été envoyé à{' '}
                  <strong>{activationInfo.email}</strong>.
                </p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Prochaines étapes
                </h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Le cavalier reçoit un email avec un lien d'activation</li>
                  <li>En cliquant sur le lien, il crée son mot de passe</li>
                  <li>Il peut ensuite se connecter à son compte</li>
                </ol>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  ⏳ Le lien d'activation est valable jusqu'au <strong>{activationInfo.expiresAt}</strong>
                </p>
              </div>

              <Button
                className="w-full"
                onClick={() => {
                  setShowActivationModal(false);
                  setActivationInfo(null);
                }}
              >
                Fermer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
